import { useCallback, useMemo, useState } from 'react'
import { App, Button, Select, Space, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatMoneyValue } from '@/shared/lib/money'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { SupplierFormModal } from '@/features/supplier/SupplierFormModal'
import {
  SUPPLIER_CATEGORIES,
  fetchSupplier,
  fetchSupplierPage,
  type Supplier,
  type SupplierCategory,
  type SupplierRow,
} from '@/features/supplier/supplierApi'

type ActiveFilter = 'ALL' | 'ACTIVE' | 'INACTIVE'

export function SuppliersPage() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [category, setCategory] = useState<SupplierCategory | null>(null)
  const [active, setActive] = useState<ActiveFilter>('ACTIVE')
  const [editing, setEditing] = useState<{ supplier: Supplier | null } | null>(null)

  const filter = useMemo(
    () => ({ category, active: active === 'ALL' ? null : active === 'ACTIVE' }),
    [category, active],
  )
  const fetchPage = useMemo(() => fetchSupplierPage(filter), [filter])

  const edit = useCallback(
    async (id: string) => {
      try {
        const supplier = await queryClient.fetchQuery({
          queryKey: queryKeys.supplier.detail(id),
          queryFn: ({ signal }) => fetchSupplier(id, signal),
        })
        setEditing({ supplier })
      } catch (error) {
        void message.error(translateError(error, t))
      }
    },
    [queryClient, message, t],
  )

  const columns = useMemo<ColumnsType<SupplierRow>>(
    () => [
      { key: 'code', title: t('supplier.field.code'), width: 120, render: (_, row) => row.code },
      {
        key: 'name',
        title: t('supplier.field.name'),
        width: 240,
        render: (_, row) => <Link to={`/suppliers/${row.id}`}>{row.name}</Link>,
      },
      { key: 'category', title: t('supplier.field.category'), width: 200, render: (_, row) => t(`supplier.category.${row.category}`) },
      { key: 'contact', title: t('supplier.field.contactPerson'), width: 180, render: (_, row) => row.contactPerson ?? t('common.dash') },
      { key: 'phone', title: t('supplier.field.phone'), width: 150, render: (_, row) => row.phone ?? t('common.dash') },
      {
        key: 'outstanding',
        title: t('supplier.summary.outstanding'),
        width: 150,
        align: 'right',
        render: (_, row) => formatMoneyValue(row.outstanding),
      },
      {
        key: 'overdue',
        title: t('supplier.summary.overdue'),
        width: 150,
        align: 'right',
        render: (_, row) =>
          row.overdue.amount > 0 ? (
            <Typography.Text type="danger">{formatMoneyValue(row.overdue)}</Typography.Text>
          ) : (
            formatMoneyValue(row.overdue)
          ),
      },
      {
        key: 'active',
        title: t('supplier.field.active'),
        width: 110,
        render: (_, row) =>
          row.active ? <Tag color="green">{t('supplier.state.active')}</Tag> : <Tag>{t('supplier.state.inactive')}</Tag>,
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          <RowActions
            items={[
              { key: 'open', label: t('supplier.action.open'), icon: <EyeOutlined />, onClick: () => navigate(`/suppliers/${row.id}`) },
              {
                key: 'edit',
                label: t('supplier.action.edit'),
                icon: <EditOutlined />,
                permission: 'supplier.manage',
                onClick: () => void edit(row.id),
              },
            ]}
          />
        ),
      },
    ],
    [t, navigate, edit],
  )

  return (
    <>
      <PageHeader
        title={t('nav.suppliers')}
        description={t('pageIntro.suppliers')}
        extra={
          <Can permission="supplier.manage">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing({ supplier: null })}>
              {t('supplier.action.create')}
            </Button>
          </Can>
        }
      />
      <Space wrap style={{ marginBottom: 16 }}>
        <Select<SupplierCategory | 'ALL'>
          style={{ minWidth: 220 }}
          value={category ?? 'ALL'}
          onChange={(value) => setCategory(value === 'ALL' ? null : value)}
          options={[
            { value: 'ALL', label: t('supplier.allCategories') },
            ...SUPPLIER_CATEGORIES.map((value) => ({ value, label: t(`supplier.category.${value}`) })),
          ]}
        />
        <Select<ActiveFilter>
          style={{ minWidth: 160 }}
          value={active}
          onChange={setActive}
          options={[
            { value: 'ACTIVE', label: t('supplier.state.active') },
            { value: 'INACTIVE', label: t('supplier.state.inactive') },
            { value: 'ALL', label: t('supplier.state.all') },
          ]}
        />
      </Space>
      <DataTable<SupplierRow>
        queryKey={[...queryKeys.supplier.suppliers, filter]}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="suppliers"
        searchable
        searchPlaceholder={t('supplier.search')}
        scrollX={1400}
        stickyFirstColumn
        emptyTitle={t('supplier.empty')}
      />
      <SupplierFormModal
        open={editing !== null}
        supplier={editing?.supplier ?? null}
        onClose={() => setEditing(null)}
        onSaved={(saved) => {
          if (editing?.supplier === null) navigate(`/suppliers/${saved.id}`)
        }}
      />
    </>
  )
}
